import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDropDown } from '../../components/icons';
import { SelectObject } from '../../types/certificates';
import cn from '../cn';
import { getPopoverPositionStyle } from '../popoverPosition';
import { useAnchorRect } from '../useAnchorRect';

type SelectUIProps = {
  selectedItem?: string | number | null,
  width: number,
  widthPercentage?: boolean,
  filter_by_text: string,
  options?: string[],
  optionsObject?: SelectObject[],
  onSelectedItem: Function,
  customSelectedText?: string,
  required?: boolean,
  disabled?: boolean,
  children?: React.ReactNode,
};

// MUI standard <Select> port: click to open a portaled listbox under the trigger,
// click-outside / Escape to close, basic arrow-key nav. Emits the MUI-shaped
// event onSelectedItem({ target: { value } }) — value is the option string or the
// object id, matching the old wrapper.
const SelectUI: FunctionComponent<SelectUIProps> = React.memo(({
  selectedItem,
  required = false,
  width,
  filter_by_text,
  options,
  optionsObject,
  onSelectedItem,
  widthPercentage = false,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const containerStyle = widthPercentage === false
    ? { minWidth: width + 'px', maxWidth: width + 'px' }
    : { width: width + '%' };

  const items: { value: string | number; label: string }[] = options !== undefined
    ? options.map((o) => ({ value: o, label: o }))
    : (optionsObject ?? []).map((o) => ({ value: o.id, label: o.name }));

  const displayText = optionsObject !== undefined
    ? optionsObject.find((o) => o.id === selectedItem)?.name ?? ''
    : (selectedItem as string) ?? '';

  // Live rect: follows the anchor when a dialog body or the page scrolls.
  const rect = useAnchorRect(open, rootRef);

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      if (listRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  const choose = (value: string | number) => {
    onSelectedItem({ target: { value } });
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) { e.preventDefault(); setOpen(true); return; }
    if (!open) return;
    if (e.key === 'Escape') { setOpen(false); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); choose(items[active].value); }
  };

  const popoverPosition = rect ? getPopoverPositionStyle(rect) : null;

  return (
    <div ref={rootRef} style={containerStyle} className="ds-control-root relative inline-flex flex-col align-top">
      <label className="ds-label">
        {filter_by_text}{required ? ' *' : ''}
      </label>

      <div
        role="combobox"
        aria-expanded={open}
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        className={cn('ds-field flex items-center justify-between',
          disabled ? 'cursor-default text-black/38' : 'cursor-pointer')}
      >
        <span className="truncate">{displayText}</span>
        <ArrowDropDown className="text-black/54" />
      </div>

      {open && rect && createPortal(
        <ul
          ref={listRef}
          role="listbox"
          style={popoverPosition!.style}
          className={cn('ds-menu scrollbar-thin', popoverPosition!.className)}
        >
          {items.map((it, i) => (
            <li
              key={i}
              role="option"
              aria-selected={selectedItem === it.value}
              data-ds-active={active === i ? 'true' : undefined}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(it.value)}
              className="ds-menu-item"
            >
              {it.label}
            </li>
          ))}
        </ul>,
        document.getElementById('root') ?? document.body,
      )}
    </div>
  );
});

export default SelectUI;
